import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { useContext } from 'react';
import type {
  MenuProps as ReactSelectMenuProps,
  OptionTypeBase,
} from 'react-select';

import { PopoverContent, PopoverOverlay, Portal } from '@frontend/components';

import { SelectContext } from '../SelectContext';
import { useSelectStyles } from '../SelectStylesContext';
import { BASE_CLASS_NAME } from '../const';

const POPOVER_STYLES_RESET = {
  display: 'initial',
  width: 'initial',
  minWidth: 'initial',
  maxWidth: 'initial',
  height: 'initial',
  minHeight: 'initial',
  maxHeight: 'initial',
  padding: 0,
  borderWidth: 0,
  borderRadius: 0,
  outlineWidth: 0,
  margin: 0,
  background: 'none',
  boxShadow: 'none',
};

export type MenuProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = ReactSelectMenuProps<OptionType, IsMulti> & {
  innerProps: HTMLChakraProps<'div'>;
};

export const Menu = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
>({
  children,
  innerProps,
  innerRef,
}: MenuProps<OptionType, IsMulti>) => {
  const { menu: menuStyles } = useSelectStyles();

  const { portalContainerRef, isPopoverOverlayEnabled } =
    useContext(SelectContext);

  return (
    <Portal containerRef={portalContainerRef}>
      {isPopoverOverlayEnabled && <PopoverOverlay />}
      <chakra.div
        __css={{
          ...menuStyles,
          '.chakra-popover__popper': POPOVER_STYLES_RESET,
          '.chakra-popover__content': POPOVER_STYLES_RESET,
          display: 'flex',
        }}
        {...innerProps}
        className={cx(`${BASE_CLASS_NAME}__menu`, innerProps.className)}
        ref={innerRef}
      >
        <PopoverContent w="100%">{children}</PopoverContent>
      </chakra.div>
    </Portal>
  );
};
